/**
 * Compact, model-free text fallback for the semantic storefront search.
 *
 * It ranks only the generated title/tag allowlist. Every meaningful query token
 * must match, so an unrelated query stays empty instead of being padded with
 * arbitrary catalogue items.
 */
(function () {
  var PAGE_SIZE = 24;
  var STOP_WORDS = {
    a: true, an: true, and: true, der: true, die: true, das: true,
    den: true, dem: true, des: true, ein: true, eine: true, einer: true,
    eines: true, for: true, fuer: true, fur: true, im: true, in: true,
    mit: true, of: true, oder: true, or: true, the: true, to: true,
    und: true, von: true, with: true, zur: true, zum: true,
  };

  function normalize(value) {
    var normalized = String(value == null ? '' : value).toLowerCase();
    if (normalized.normalize) normalized = normalized.normalize('NFKD');
    return normalized
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function tokens(value) {
    var normalized = normalize(value);
    if (!normalized) return [];
    return normalized.split(' ').filter(function (token) {
      return token && !STOP_WORDS[token];
    });
  }

  function boundedDistance(left, right, limit) {
    if (Math.abs(left.length - right.length) > limit) return limit + 1;
    var previous = [];
    var current = [];
    var i;
    var j;
    for (j = 0; j <= right.length; j += 1) previous[j] = j;
    for (i = 1; i <= left.length; i += 1) {
      current[0] = i;
      var rowMinimum = current[0];
      for (j = 1; j <= right.length; j += 1) {
        current[j] = Math.min(
          current[j - 1] + 1,
          previous[j] + 1,
          previous[j - 1] + (left.charAt(i - 1) === right.charAt(j - 1) ? 0 : 1)
        );
        rowMinimum = Math.min(rowMinimum, current[j]);
      }
      if (rowMinimum > limit) return limit + 1;
      previous = current.slice();
    }
    return previous[right.length];
  }

  function tokenMatch(queryToken, productToken) {
    if (queryToken === productToken) return 100;
    if (
      queryToken.length >= 4 &&
      productToken.length >= 4 &&
      (productToken.indexOf(queryToken) === 0 || queryToken.indexOf(productToken) === 0)
    ) {
      return 68;
    }
    var limit = queryToken.length >= 8 ? 2 : (queryToken.length >= 4 ? 1 : 0);
    if (!limit) return 0;
    var distance = boundedDistance(queryToken, productToken, limit);
    return distance <= limit ? 56 - (distance * 8) : 0;
  }

  function fieldScore(queryToken, fieldTokens, weight) {
    var best = 0;
    fieldTokens.forEach(function (productToken) {
      best = Math.max(best, tokenMatch(queryToken, productToken));
    });
    return best * weight;
  }

  function rankProduct(product, query, queryTokens) {
    var titleNormalized = normalize(product.title);
    var titleTokens = tokens(product.title);
    var tagTokens = [];
    (product.tags || []).forEach(function (tag) {
      tagTokens = tagTokens.concat(tokens(tag));
    });

    var score = titleNormalized.indexOf(query) !== -1 ? 180 : 0;
    for (var i = 0; i < queryTokens.length; i += 1) {
      var titleScore = fieldScore(queryTokens[i], titleTokens, 4);
      var tagScore = fieldScore(queryTokens[i], tagTokens, 3);
      var best = Math.max(titleScore, tagScore);
      if (!best) return null;
      score += best;
    }
    return score;
  }

  function indexData() {
    var index = window.DubistdaSearchFallbackIndex;
    if (
      !index ||
      !Array.isArray(index.products) ||
      typeof index.revision !== 'string' ||
      index.n_products !== index.products.length
    ) {
      return null;
    }
    return index;
  }

  function ranked(query) {
    var index = indexData();
    var normalizedQuery = normalize(query);
    var queryTokens = tokens(query);
    if (!index || !normalizedQuery || queryTokens.length === 0) return [];

    return index.products
      .map(function (product) {
        return { product: product, score: rankProduct(product, normalizedQuery, queryTokens) };
      })
      .filter(function (entry) { return entry.score !== null; })
      .sort(function (left, right) {
        if (right.score !== left.score) return right.score - left.score;
        return left.product.handle < right.product.handle ? -1 :
          (left.product.handle > right.product.handle ? 1 : 0);
      });
  }

  function cursorOffset(cursor, revision) {
    if (cursor == null) return 0;
    if (typeof cursor !== 'string') return null;
    var match = /^fb:([a-f0-9]{64}):([0-9]+)$/.exec(cursor);
    if (!match || 'sha256:' + match[1] !== revision) return null;
    var offset = Number(match[2]);
    return Number.isSafeInteger(offset) && offset >= 0 ? offset : null;
  }

  function makeCursor(revision, offset) {
    return 'fb:' + revision.replace(/^sha256:/, '') + ':' + offset;
  }

  function search(query, options) {
    var index = indexData();
    var opts = options || {};
    var limit = Number.isSafeInteger(opts.limit) ?
      Math.max(1, Math.min(PAGE_SIZE, opts.limit)) : PAGE_SIZE;
    var excluded = Object.create(null);
    (opts.exclude || []).forEach(function (handle) { excluded[String(handle)] = true; });
    var matches = ranked(query).filter(function (entry) {
      return !excluded[entry.product.handle];
    });
    var offset = index ? cursorOffset(opts.cursor, index.revision) : 0;
    if (offset === null) {
      return {
        built_at: index ? index.built_at : null,
        next_cursor: null,
        results: [],
        revision: index ? index.revision : null,
        total: matches.length,
      };
    }
    if (offset > matches.length) offset = matches.length;
    var page = matches.slice(offset, offset + limit);
    var nextOffset = offset + page.length;
    return {
      built_at: index ? index.built_at : null,
      next_cursor: nextOffset < matches.length && index ?
        makeCursor(index.revision, nextOffset) : null,
      results: page.map(function (entry) { return entry.product; }),
      revision: index ? index.revision : null,
      total: matches.length,
    };
  }

  function productByHandle(handle) {
    var index = indexData();
    if (!index) return null;
    for (var i = 0; i < index.products.length; i += 1) {
      if (index.products[i].handle === handle) return index.products[i];
    }
    return null;
  }

  function cardProduct(product, storefrontPath) {
    if (!product || typeof product.handle !== 'string') return null;
    return {
      featured_image: product.featured_image || '',
      handle: product.handle,
      price: typeof product.price === 'number' ? product.price : null,
      tags: product.tags || [],
      title: product.title || '',
      url: storefrontPath('products/' + encodeURIComponent(product.handle)),
    };
  }

  window.DubistdaSearchFallback = {
    PAGE_SIZE: PAGE_SIZE,
    cardProduct: cardProduct,
    normalize: normalize,
    productByHandle: productByHandle,
    search: search,
  };
})();
